import React, { useEffect, useState, useMemo } from 'react';
import { FormControl as Input, Pagination, Message } from 'tinper-bee';
import * as util from '../../utils';
import SkillCard, { formatNumber } from './SkillCard';
import './index.less';
import Layout from '../Layout';

interface Skill {
  id: string;
  name: string;
  version: string;
  description: string;
  skillDescription?: string;
  author?: string;
  creatorName?: string;
  download?: number;
  downloads?: number;
  tags?: string[];
  latestTime?: string;
  [key: string]: any;
}

type MainTab = 'list' | 'guide';
type GuideTab = 'install' | 'publish';

const PAGE_SIZE = 10;

const PluginMarket: React.FC = () => {
  const [mainTab, setMainTab] = useState<MainTab>('list');
  const [guideTab, setGuideTab] = useState<GuideTab>('install');

  const [skills, setSkills] = useState<Skill[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [hotDownloadData, setHotDownloadData] = useState<Skill[]>([]);

  useEffect(() => {
    fetchSkills(1, '');
  }, []);

  useEffect(() => {
    fetchRanking();
  }, []);

  const fetchSkills = async (page: number = pageNumber, search: string = searchValue) => {
    setLoading(true);
    try {
      const response = await util.get('/package/list', {
        type: 'plugin',
        search: search || undefined,
        pageNumber: page,
        pageSize: PAGE_SIZE,
        hotOrNew :1
      });
      if (response && response.data) {
        setSkills(response.data.list || []);
        setTotalCount(response.data.pageInfo?.count || 0);
      }
    } catch (error) {
      console.error('Failed to fetch skills:', error);
      Message.create({ content: '获取插件列表失败', color: 'danger' });
    } finally {
      setLoading(false);
    }
  };

  const fetchRanking = async () => {
    try {
      const response = await util.get('/package/list', {
        type: 'plugin',
        pageNumber: 1,
        pageSize: 10,
        sort: 'download'
      });
      if (response?.data) {
        const list = response.data.list || [];
        setHotDownloadData(list);
      }
    } catch (error) {
      console.error('Failed to fetch ranking:', error);
    }
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    setPageNumber(1);
    fetchSkills(1, value);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch(searchValue);
    }
  };

  const pageChange = (page: number) => {
    setPageNumber(page);
    fetchSkills(page);
    window.scrollTo(0, 0);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      Message.create({ content: '已复制到剪贴板', color: 'success' });
    }).catch(() => {
      Message.create({ content: '复制失败', color: 'danger' });
    });
  };

  const currentRankingData = hotDownloadData;

  return (
    <div className="plugin-market-page">
      <div className="skill-market-container wrap">

        {/* Main Tabs */}
        <div className="main-tabs">
          <button
            className={`main-tab ${mainTab === 'list' ? 'active' : ''}`}
            onClick={() => setMainTab('list')}
          >
            Plugin 列表 ({totalCount})
          </button>
          <button
            className={`main-tab ${mainTab === 'guide' ? 'active' : ''}`}
            onClick={() => setMainTab('guide')}
          >
            使用指南
          </button>
        </div>

        <div className="content-layout">
          {/* Left Area */}
          <div className="content-left">
            {mainTab === 'list' && (
              <>
                {/* Search */}
                <div className="search-bar">
                  <span className="search-icon">🔍</span>
                  <Input
                    placeholder="搜索插件名称..."
                    value={searchValue}
                    onChange={(e: any) => handleSearch(e)}
                    onKeyDown={handleSearchKeyDown}
                    className="search-input"
                  />
                  {searchValue && (
                    <button
                      className="clear-btn"
                      onClick={() => handleSearch('')}
                      title="清除搜索"
                    >
                      ✕
                    </button>
                  )}
                </div>

                {/* Skill Grid */}
                {loading ? (
                  <div className="skill-loading">
                    <div className="loading-spinner"></div>
                    <p>加载中...</p>
                  </div>
                ) : skills.length > 0 ? (
                  <>
                    <div className="plugin-grid">
                      {skills.map((skill, index) => (
                        <SkillCard key={skill.id} skill={skill} index={index} />
                      ))}
                    </div>
                    {totalCount > PAGE_SIZE && (
                      <div className="pagination-wrapper">
                        <Pagination
                          first
                          last
                          prev
                          next
                          maxButtons={5}
                          boundaryLinks
                          activePage={pageNumber}
                          onSelect={pageChange}
                          showJump={true}
                          total={totalCount}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <div className="skill-empty">
                    <div className="empty-icon">🔍</div>
                    <p className="empty-text">
                      {searchValue ? `未找到与 "${searchValue}" 相关的插件` : '暂无插件'}
                    </p>
                    <p className="empty-hint">
                      {searchValue ? '尝试使用其他关键词搜索' : '稍后再来看看'}
                    </p>
                  </div>
                )}
              </>
            )}

            {mainTab === 'guide' && (
              <div className="guide-section">
                <div className="guide-tabs">
                  <button
                    className={`guide-tab ${guideTab === 'install' ? 'active' : ''}`}
                    onClick={() => setGuideTab('install')}
                  >
                    <span className="tab-icon">📥</span>
                    <span>下载插件</span>
                  </button>
                  <button
                    className={`guide-tab ${guideTab === 'publish' ? 'active' : ''}`}
                    onClick={() => setGuideTab('publish')}
                  >
                    <span className="tab-icon">📤</span>
                    <span>发布插件</span>
                  </button>
                </div>

                <div className="guide-content">
                  {guideTab === 'install' && (
                    <div className="guide-panel">
                      <div className="guide-steps">
                        <div className="step-item">
                          <div className="step-header">
                            <div className="step-number">1</div>
                            <h4 className="step-title">安装 YNPM</h4>
                          </div>
                          <div className="step-content">
                            <p className="step-desc">首先确保你已经安装了 ynpm-tool@5.13.21 以上版本命令行工具</p>
                            <div className="code-box">
                              <button className="copy-btn-small" onClick={() => copyToClipboard('npm install -g ynpm-tool --registry https://registry.npmjs.org/')}>复制</button>
                              <pre><code>npm install -g ynpm-tool --registry https://registry.npmjs.org/</code></pre>
                            </div>
                          </div>
                        </div>

                        <div className="step-item">
                          <div className="step-header">
                            <div className="step-number">2</div>
                            <h4 className="step-title">搜索并下载插件</h4>
                          </div>
                          <div className="step-content">
                            <p className="step-desc">在插件市场找到需要的插件,使用 download 命令下载</p>
                            <div className="code-box">
                              <button className="copy-btn-small" onClick={() => copyToClipboard('ynpm download --plugin superpowers@2.1.0')}>复制</button>
                              <pre><code>{`# 下载最新版本
ynpm download --plugin superpowers

# 下载指定版本(推荐用于生产环境)
ynpm download --plugin superpowers@2.1.0

# 下载到指定目录
ynpm download --plugin superpowers --dir ./plugins
`}</code></pre>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {guideTab === 'publish' && (
                    <div className="guide-panel">
                      <div className="guide-steps">
                        <div className="step-item">
                          <div className="step-header">
                            <div className="step-number">1</div>
                            <h4 className="step-title">准备工作</h4>
                          </div>
                          <div className="step-content">
                            <p className="step-desc">确保你的插件包含必要的文件</p>
                            <div className="code-box">
                              <pre><code>{`package.json
plugin.json
workflows/
agents/
skills/
hooks/`}</code></pre>
                            </div>
                          </div>
                        </div>

                        <div className="step-item">
                          <div className="step-header">
                            <div className="step-number">2</div>
                            <h4 className="step-title">登录并发布</h4>
                          </div>
                          <div className="step-content">
                            <p className="step-desc">登录你的账号并发布插件到市场</p>
                            <div className="code-box">
                              <button className="copy-btn-small" onClick={() => copyToClipboard('ynpm publish --plugin')}>复制</button>
                              <pre><code>{`# 登录 ynpm 账号
ynpm login

# 发布插件
ynpm publish --plugin`}</code></pre>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right Area - Hot Rankings */}
          <div className="content-right">
            <div className="ranking-card">
              <div className="ranking-header">
                <span className="ranking-title">🔥 热门插件排行</span>
              </div>
              <div className="ranking-tabs">
                <button
                  className="ranking-tab active"
                >
                  下载量
                </button>
              </div>
              <div className="ranking-list">
                {currentRankingData.length > 0 ? (
                  currentRankingData.map((skill, idx) => (
                    <div
                      key={skill.id}
                      className={`ranking-item ${idx < 3 ? 'top-three' : ''}`}
                      onClick={() => window.location.href = `/plugin-market/detail/${skill.name}`}
                    >
                      <span className={`ranking-index index-${idx + 1}`}>{idx + 1}</span>
                      <span className="ranking-name" title={skill.name}>{skill.name}</span>
                      <span className="ranking-value">
                        {formatNumber(skill.download || 0)}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="ranking-empty">暂无数据</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Layout(PluginMarket, 'plugin-market-page');
